import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Title2, Body1, makeStyles, tokens } from '@fluentui/react-components';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './layout/Sidebar';
import Breadcrumbs from './layout/Breadcrumbs';

const useStyles = makeStyles({
  header: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tabsRow: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
  },
  tab: {
    borderBottom: '2px solid transparent',
    color: tokens.colorNeutralForeground2,
    ':hover': {
      color: tokens.colorNeutralForeground1,
      borderBottomColor: tokens.colorNeutralStroke1,
    },
  },
  tabActive: {
    borderBottomColor: tokens.colorBrandStroke1,
    color: tokens.colorBrandForeground1,
    ':hover': {
      color: tokens.colorBrandForeground1,
      borderBottomColor: tokens.colorBrandStroke1,
    },
  },
});

export default function Layout({
  children,
  breadcrumbs = [],
  pageTitle,
  pageDescription,
  tabs = [],
  currentTab,
  headerAction
}) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const styles = useStyles();

  // Public pages (no sidebar)
  const isPublicPage = ['/', '/login', '/register', '/privacy-policy', '/terms-of-service'].includes(location.pathname);

  if (isPublicPage) {
    return (
      <div className="min-h-screen bg-bg-secondary">
        {children}
      </div>
    );
  }

  // Protected pages (with sidebar)
  return (
    <div className="flex h-screen bg-bg-secondary">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className={styles.header}>
          {/* Top Row: Title & Action */}
          <div className="flex items-center justify-between px-4 md:px-8 py-4">
            <div className="flex items-center gap-4 flex-1">
              {/* Page Title and Description */}
              {pageTitle ? (
                <div className="flex-1">
                  <Title2 as="h1" block>{pageTitle}</Title2>
                  {pageDescription && (
                    <Body1 as="p" block className={`mt-1 ${styles.subtitle}`}>{pageDescription}</Body1>
                  )}
                </div>
              ) : (
                breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />
              )}
            </div>

            {/* Header Action */}
            {headerAction && (
              <div className="flex items-center gap-4">
                {headerAction}
              </div>
            )}
          </div>

          {/* Tabs Row */}
          {tabs.length > 0 && (
            <div className={styles.tabsRow}>
              <nav className="flex space-x-1 px-6" aria-label="Tabs">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  const isActive = currentTab === tab.id;
                  return (
                    <Link
                      key={tab.id}
                      to={tab.path}
                      aria-current={isActive ? 'page' : undefined}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors ${isActive ? styles.tabActive : styles.tab}`}
                    >
                      <IconComponent className="h-5 w-5" />
                      <span>{tab.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
